/**
 * Auth state via React context. Mirrors iOS AuthState — three statuses
 * (checking, signedOut, signedIn) drive the root route guard.
 *
 * Bootstrap behavior: on first mount, read tokens from localStorage. If
 * present and refresh isn't expired, transition to signedIn. If refresh
 * IS expired, clear and go to signedOut. Access token refresh happens
 * lazily on the first 401 from the API layer, not here.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";

import { registerForceSignOut } from "@/shared/api";
import { signOut as serverSignOut } from "@/features/auth/lib/authClient";
import {
  clearTokens,
  loadTokens,
  saveTokens,
  type StoredTokens,
} from "@/features/auth/lib/tokenStorage";

export type AuthStatus =
  | { kind: "checking" }
  | { kind: "signedOut" }
  | { kind: "signedIn"; userId: string };

interface AuthContextValue {
  status: AuthStatus;
  /** Last sign-in error string, displayed on the sign-in page. */
  signInError: string | null;
  setSignInError: (msg: string | null) => void;

  /** Transition to signedIn after a successful AuthClient.signIn. */
  setSignedIn: (tokens: StoredTokens) => void;

  /** Server-side revoke + local clear. */
  signOut: () => Promise<void>;

  /** Immediate local clear, no server call. Used when a refresh fails
   *  and we know the session is dead. */
  forceSignOut: () => void;

  /** Update tokens after a successful refresh. */
  updateTokens: (tokens: StoredTokens) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Top-level auth state provider. Mount once at the app root; every
 * authenticated route reads via `useAuth()`. Bootstraps `status` from
 * localStorage on first mount and wires the API client's force-sign-out
 * hook so token-refresh failures kick the user back to /sign-in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>({ kind: "checking" });
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    const tokens = loadTokens();
    if (!tokens) {
      setStatus({ kind: "signedOut" });
      return;
    }
    // Refresh-token expiry is the hard floor — past that, full re-auth.
    if (new Date(tokens.refreshTokenExpiresAt).getTime() < Date.now()) {
      clearTokens();
      setStatus({ kind: "signedOut" });
      return;
    }
    setStatus({ kind: "signedIn", userId: tokens.userId });
    Sentry.setUser({ id: tokens.userId });
  }, []);

  const setSignedIn = useCallback((tokens: StoredTokens) => {
    saveTokens(tokens);
    setStatus({ kind: "signedIn", userId: tokens.userId });
    setSignInError(null);
    Sentry.setUser({ id: tokens.userId });
  }, []);

  const updateTokens = useCallback((tokens: StoredTokens) => {
    saveTokens(tokens);
    // user_id doesn't change on refresh, but kept consistent regardless.
    setStatus({ kind: "signedIn", userId: tokens.userId });
    Sentry.setUser({ id: tokens.userId });
  }, []);

  const forceSignOut = useCallback(() => {
    clearTokens();
    setStatus({ kind: "signedOut" });
    Sentry.setUser(null);
  }, []);

  // Wire the API client's "session expired, kick to sign-in" hook
  // to our state. Done in an effect so the handler captures the
  // latest setStatus closure.
  useEffect(() => {
    registerForceSignOut(forceSignOut);
  }, [forceSignOut]);

  const signOut = useCallback(async () => {
    const tokens = loadTokens();
    if (tokens) {
      await serverSignOut(tokens.refreshToken);
    }
    clearTokens();
    setStatus({ kind: "signedOut" });
    Sentry.setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      signInError,
      setSignInError,
      setSignedIn,
      signOut,
      forceSignOut,
      updateTokens,
    }),
    [status, signInError, setSignedIn, signOut, forceSignOut, updateTokens],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook for reading + mutating auth state from any descendant of
 * `<AuthProvider>`. Throws if used outside the provider, so
 * mis-mounted components fail loudly rather than getting `null`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
